import { useState } from "react";
import CheckBox from "@mui/icons-material/CheckBox";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { v4 } from "uuid";

import type { ExtendedPatient } from "@thalamos/aspire-common";

import { SoadCreatePatient } from "./SoadCreatePatient.js";
import { SoadNhsNumberSearch } from "./SoadNhsNumberSearch.js";
import { api } from "~/api.js";
import {
  Banner,
  BannerList,
  Button,
  FormTitle,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
  TextField,
} from "~/components/design-system/index.js";
import { FormFooterSection } from "~/components/form/FormFooter.js";
import { TrainingBanner } from "~/components/form/TrainingBanner/TrainingBanner.js";
import { routeFns } from "~/routePaths.js";

export type NewSoadAssessmentProps = Record<string, never>;

export default function NewSoadAssessment(_props: NewSoadAssessmentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [proceedManualInputPopup, setProceedManualInputPopup] = useState(false);

  const [showPatientDetails, setShowPatientDetails] = useState(false);

  const [requestId, setRequestId] = useState<string | null>(null);
  const [patientEntryMode, setPatientEntryMode] = useState<
    "search-by-nhs-number" | "create" | null
  >(null);

  const [selectedPatient, setSelectedPatient] =
    useState<ExtendedPatient | null>(null);

  const requestIdIsValid = requestId !== null && requestId.length >= 6;

  const showSoadCreatePatient =
    requestIdIsValid &&
    showPatientDetails &&
    (patientEntryMode === "create" || selectedPatient);

  if (showSoadCreatePatient) {
    return (
      <Stack
        sx={{
          gap: 1,
        }}
      >
        <FormTitle
          useReducedTopPadding={true}
          hasTitleBottomMargin={false}
          titleText={t("pages.newSoadAssessment.patientDetails.title")}
        />
        <TrainingBanner />
        <SoadCreatePatient
          selectedPatient={selectedPatient}
          setSelectedPatient={setSelectedPatient}
          onContinue={(patientId) => {
            const runAsync = async () => {
              const formContextId = v4();
              const createResponse =
                await api.forms.createSoadAssessmentContext(
                  formContextId,
                  patientId,
                  requestId,
                );

              if (createResponse.status === 204) {
                navigate(routeFns.formContextPage(formContextId, patientId));
              } else {
                renderErrorToast({
                  message:
                    "Failed to create form context - please try again later",
                });
              }
            };
            void runAsync();
          }}
          setShowPatientDetails={setShowPatientDetails}
          setPatientEntryMode={setPatientEntryMode}
        />
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        gap: 1,
      }}
    >
      <ManualInputWarningBanner
        proceedManualInputPopup={proceedManualInputPopup}
        setProceedManualInputPopup={setProceedManualInputPopup}
        setPatientEntryMode={setPatientEntryMode}
        setSelectedPatient={setSelectedPatient}
        setShowPatientDetails={setShowPatientDetails}
      />
      <FormTitle
        useReducedTopPadding={true}
        hasTitleBottomMargin={false}
        titleText={t("pages.newSoadAssessment.title")}
      />
      <TrainingBanner />
      <Banner
        bannerType={BannerList.INFO}
        title={t("pages.newSoadAssessment.assessment")}
        body={t("pages.newSoadAssessment.assessmentBannerInfo")}
      />
      <Box
        sx={{
          marginTop: "2rem",
          display: "flex",
          gap: "1rem",
          alignItems: "center",
        }}
      >
        <TextField
          name="requestId"
          label={t("pages.newSoadAssessment.caseReference")}
          value={requestId ?? undefined}
          onChange={(val) => {
            setRequestId(val);
          }}
        />
        {requestIdIsValid && <CheckBox sx={{ color: "green" }} />}
      </Box>
      {requestIdIsValid && (
        <Box sx={{ marginTop: "1rem" }}>
          <FormControl>
            <FormLabel sx={{ fontWeight: "bold" }}>
              {t("pages.newSoadAssessment.doYouHaveNhsNumber")}
            </FormLabel>
            <RadioGroup
              value={patientEntryMode ?? undefined}
              name="do-you-have-nhs-number"
              onChange={(e) => {
                setPatientEntryMode(
                  e.target.value as "search-by-nhs-number" | "create",
                );
              }}
            >
              <FormControlLabel
                value="search-by-nhs-number"
                control={<Radio />}
                label="Yes"
              />
              <FormControlLabel value="create" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        </Box>
      )}
      {requestIdIsValid &&
        patientEntryMode === "search-by-nhs-number" &&
        !selectedPatient && (
          <Box sx={{ marginTop: "1em" }}>
            <SoadNhsNumberSearch
              onPatientSelected={async (patient) => {
                setSelectedPatient(patient);
              }}
              setProceedManualInputPopup={setProceedManualInputPopup}
              setShowPatientDetails={setShowPatientDetails}
            />
          </Box>
        )}
      {requestIdIsValid && patientEntryMode === "create" && (
        <Box sx={{ marginTop: "1em" }}>
          <FormFooterSection
            onCancel={() => {}}
            discardLabel={t("buttonLabels.cancel")}
            hideDiscard={true}
            saveLabel={t("buttonLabels.continue")}
            onSave={() => {
              setProceedManualInputPopup(true);
            }}
          />
        </Box>
      )}
    </Stack>
  );
}

export function ManualInputWarningBanner({
  proceedManualInputPopup,
  setProceedManualInputPopup,
  setPatientEntryMode,
  setSelectedPatient,
  setShowPatientDetails,
}: {
  proceedManualInputPopup: boolean;
  setProceedManualInputPopup: (open: boolean) => void;
  setPatientEntryMode: (mode: "search-by-nhs-number" | "create") => void;
  setSelectedPatient: (patient: ExtendedPatient | null) => void;
  setShowPatientDetails: (show: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <PopupDialog
      open={proceedManualInputPopup}
      onClose={() => {
        setProceedManualInputPopup(false);
      }}
    >
      <PopupDialogTitle
        closeDialog={() => {
          setProceedManualInputPopup(false);
        }}
        titleText={t("pages.newSoadAssessment.manualInputWarningBanner.title")}
      />
      <Banner
        bannerType={BannerList.WARNING}
        body={[
          t(
            "pages.newSoadAssessment.manualInputWarningBanner.confirmWithoutNHSNumberWarning",
          ),
          t(
            "pages.newSoadAssessment.manualInputWarningBanner.difficultyMatchingAssessmentWarning",
          ),
          t(
            "pages.newSoadAssessment.manualInputWarningBanner.nhsNumberIrreversibleActionNote",
          ),
        ]}
      />

      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          mt: 2,
        }}
      >
        <Button
          variant="outlined"
          label={t("buttonLabels.return")}
          testId="start-new-button"
          onClick={() => {
            setProceedManualInputPopup(false);
            setSelectedPatient(null);
          }}
        />
        <Button
          label={t("buttonLabels.continue")}
          onClick={() => {
            setPatientEntryMode("create");
            setProceedManualInputPopup(false);
            setShowPatientDetails(true);
          }}
        />
      </Box>
    </PopupDialog>
  );
}
